import React, {
  createContext,
  useMemo,
} from 'react';
import {
  arrayOf,
  bool,
  node,
  shape,
  string,
  func
} from 'prop-types';

import { useDataModel } from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';

import { transformItemDataToPromoModel, buildRefinedNValue } from '../utils/promo-model-utils';
import { getItemIdLists, isBackwardsBogo, isForwardsBogo } from '../utils/promo-utils';
import { MA } from '../utils/constants';
import { isFulfillable } from '../utils/promo-fulfillment-utils';
import { PromoModelProviderDataModel } from '../models/PromoModelProviderDataModel';

export const defaultModelContext = {
  allListProductsAndAnchor: new Map(),
  analyticsComponent: '',
  analyticsSharedSection: '',
  anchorItem: null,
  applianceDeliveryStore: null,
  bogoMaxQuantity: 1,
  displayableFirstListItemIds: [],
  displayableSecondListItemIds: [],
  endDate: null,
  expDate: null,
  isAnchorFulfillable: false,
  isBackwardsBogo: false,
  isBatchComplete: false,
  isBmsm: false,
  isBogo: false,
  isDollarThresholdBogo: false,
  isExpiring: false,
  isForwardsB1gy: false,
  isForwardsBogo: false,
  isMsb: false,
  isMultiSelect: false,
  listHasFulfillment: false,
  longDesc: '',
  originalNvalue: null,
  refinedNvalue: null,
  rewardTiers: [{
    maxAllowedRewardAmount: 0,
    maxPurchaseQuantity: 0,
    minPurchaseAmount: 0,
    minPurchaseQuantity: 0,
    rewardAmountPerItem: 0,
    rewardAmountPerOrder: 0,
    rewardPercent: 0,
  }],
  shortDesc: '',
  src1EligibilityCriteria: {},
  src1MinPurchaseAmount: 0,
  startDate: null,
  experienceTag: '',
  subExperience: '',
  tgt1EligibilityCriteria: {},
  tgt1MinPurchaseAmount: 0
};

export const PromoModelContext = createContext(defaultModelContext);

export const PromoModelProvider = ({
  anchorItem,
  hasUserInteracted,
  isMultiSelect,
  checkAvailabilityFulfillment,
  children,
  type,
  onFail
}) => {
  const storeId = useStoreId();

  const isBogoCard = type === 'card';
  const subExperienceTag = anchorItem.pricing.conditionalPromotions[0].subExperienceTag;
  const originalNvalue = anchorItem.pricing.conditionalPromotions[0].nvalues?.[0];
  const brandRefinementId = anchorItem.pricing.conditionalPromotions[0].brandRefinementId;
  const experienceTag = anchorItem.pricing.conditionalPromotions[0].experienceTag;
  const refinedNvalue = buildRefinedNValue(originalNvalue, brandRefinementId);
  // in most cases, first list is the qualifying items, but in backwards bogo it is the reward
  const { firstListItemIds, secondListItemIds } = getItemIdLists(anchorItem);
  const isAnchorMajorAppliance = anchorItem.identifiers?.productType === MA;
  const isAnchorSoldOut = isAnchorMajorAppliance
    ? !checkAvailabilityFulfillment.isLoading && !checkAvailabilityFulfillment.isAvailable
    : anchorItem.fulfillment && !isFulfillable(anchorItem);
  // quantity BOGOs are disabled when the anchor is unfulfillable.
  // We will want to remove this check when we've updated all BOGOs to two list UX
  const shouldOnFailSoldOutAnchor = isForwardsBogo({ subExperienceTag }) || isBackwardsBogo({ subExperienceTag });
  const shouldWaitForUserInteraction = !isBogoCard;
  const skipListRequest = (shouldOnFailSoldOutAnchor && isAnchorSoldOut)
    || (shouldWaitForUserInteraction && !hasUserInteracted);

  const firstListVariables = {};

  if (refinedNvalue) {
    firstListVariables.navParam = refinedNvalue;
    // 6 items total in the drawer. 5 search items + anchor item.
    // We request 6 in the event that the anchor item is included in search results
    firstListVariables.pageSize = 6;
    firstListVariables.storefilter = 'ALL';
  } else {
    firstListVariables.itemIds = firstListItemIds;
  }

  const skipFirstListData = skipListRequest || (!firstListItemIds.length && !refinedNvalue);
  const { data: firstListData, loading: firstListLoading } = useDataModel('searchModel', {
    variables: {
      storeId,
      ...firstListVariables,
    },
    skip: skipFirstListData,
    ssr: false
  });

  const skipSecondListData = skipListRequest || (!secondListItemIds.length);
  const { data: secondListData, loading: secondListLoading } = useDataModel('searchModel', {
    variables: {
      storeId,
      itemIds: secondListItemIds,
    },
    skip: skipSecondListData,
    ssr: false
  });

  const isLoading = firstListLoading || secondListLoading;
  const isBatchComplete = !isLoading && !skipListRequest;

  const contextValue = useMemo(() => {
    const eligibilityCriteria = anchorItem.pricing.conditionalPromotions[0].eligibilityCriteria || [];
    const promoModel = transformItemDataToPromoModel({
      firstListProducts: firstListData?.searchModel?.products,
      secondListProducts: secondListData?.searchModel?.products,
      anchorItem,
      refinedNvalue,
      isApplianceAnchorAvailable: checkAvailabilityFulfillment.isAvailable,
    });

    return {
      refinedNvalue,
      originalNvalue,
      anchorItem,
      startDate: promoModel.startDate,
      endDate: promoModel.endDate,
      expDate: promoModel.expDate,
      isExpiring: promoModel.isExpiring,
      shortDesc: promoModel.shortDesc,
      longDesc: promoModel.longDesc,
      isBatchComplete,
      allListProductsAndAnchor: promoModel.allListProductsAndAnchor,
      isForwardsB1gy: promoModel.isForwardsB1gy,
      isBogo: promoModel.isBogo,
      isMsb: promoModel.isMsb,
      isBmsm: promoModel.isBmsm,
      isForwardsBogo: promoModel.isForwardsBogo,
      isBackwardsBogo: promoModel.isBackwardsBogo,
      isDollarThresholdBogo: promoModel.isDollarThresholdBogo,
      isMultiSelect,
      analyticsComponent: promoModel.analyticsComponent,
      analyticsSharedSection: promoModel.analyticsSharedSection,
      displayableFirstListItemIds: promoModel.displayableFirstListItemIds,
      displayableSecondListItemIds: promoModel.displayableSecondListItemIds,
      listHasFulfillment: promoModel.listHasFulfillment,
      isAnchorFulfillable: promoModel.isAnchorFulfillable,
      experienceTag,
      subExperience: promoModel.subExperience,
      rewardTiers: promoModel.rewardTiers,
      bogoMaxQuantity: 1, // TODO: link to dynamic data when available
      applianceDeliveryStore: checkAvailabilityFulfillment.applianceDeliveryStore,
      src1MinPurchaseAmount: promoModel.src1MinPurchaseAmount,
      src1EligibilityCriteria: eligibilityCriteria.find(({ itemGroup }) => itemGroup === 'SRC-1') || {},
      tgt1MinPurchaseAmount: promoModel.tgt1MinPurchaseAmount,
      tgt1EligibilityCriteria: eligibilityCriteria.find(({ itemGroup }) => itemGroup === 'TGT-1') || {},
    };
  }, [
    firstListData?.searchModel?.products,
    secondListData?.searchModel?.products,
    anchorItem,
    isBatchComplete,
    refinedNvalue,
    originalNvalue,
    isMultiSelect,
    checkAvailabilityFulfillment,
    experienceTag
  ]);

  if ((shouldOnFailSoldOutAnchor && isAnchorSoldOut)
    || (isBatchComplete && !contextValue.listHasFulfillment)) {
    if (onFail) onFail();
    if (isBogoCard) return null; // pod still renders onFail scenarios
  }

  return (
    <PromoModelContext.Provider value={contextValue}>
      {children}
    </PromoModelContext.Provider>
  );
};

PromoModelProvider.displayName = 'PromoModelProvider';

PromoModelProvider.propTypes = {
  anchorItem: shape({
    pricing: shape({
      conditionalPromotions: arrayOf(shape({
        experienceTag: string.isRequired,
      }).isRequired).isRequired
    }).isRequired
  }).isRequired,
  hasUserInteracted: bool.isRequired,
  isMultiSelect: bool.isRequired,
  checkAvailabilityFulfillment: shape({
    isAvailable: bool.isRequired,
    applianceDeliveryStore: string,
  }).isRequired,
  children: node.isRequired,
  type: string.isRequired,
  onFail: func
};

PromoModelProvider.defaultProps = {
  onFail: null
};

PromoModelProvider.dataModel = PromoModelProviderDataModel;
